/**
 * ActionMenuDropdown — generic floating action menu used by row-level "…" menus.
 *
 * Keep this primitive feature-agnostic. Callers (friends leaderboard row menu,
 * World Cup leaderboard row menu, etc.) assemble their `actions` array and pass
 * it in.
 */

import React, { useMemo } from 'react';
import { View, Text, Modal, Pressable } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import {
  getActionMenuDropdownStyles,
  getActionMenuDropdownPosition,
} from './ActionMenuDropdown.styles';

export interface ActionMenuAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Short explanation shown below the label when disabled. */
  reason?: string;
  /** Render the label in the theme's error color. */
  danger?: boolean;
  onPress: () => void;
}

export interface ActionMenuDropdownProps {
  visible: boolean;
  actions: ReadonlyArray<ActionMenuAction>;
  /** Absolute position of the dropdown (top/right from the trigger). */
  position: { top: number; right: number };
  onClose: () => void;
}

export const ActionMenuDropdown: React.FC<ActionMenuDropdownProps> = ({
  visible,
  actions,
  position,
  onClose,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getActionMenuDropdownStyles(theme), [theme]);

  if (!visible) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <View
          style={[styles.dropdown, getActionMenuDropdownPosition(position.top, position.right)]}
          onStartShouldSetResponder={() => true}
        >
          {actions.map((action) => {
            const rowStyle = action.disabled ? styles.optionDisabled : styles.option;
            const labelStyle = action.disabled
              ? styles.optionTextDisabled
              : action.danger
                ? styles.optionTextDanger
                : styles.optionText;

            return (
              <Pressable
                key={action.id}
                style={rowStyle}
                disabled={action.disabled}
                onPress={() => {
                  if (action.disabled) return;
                  onClose();
                  action.onPress();
                }}
              >
                {action.icon ? <View>{action.icon}</View> : null}
                <View style={styles.optionTextBlock}>
                  <Text style={labelStyle}>{action.label}</Text>
                  {action.disabled && action.reason ? (
                    <Text style={styles.optionReason}>{action.reason}</Text>
                  ) : null}
                </View>
              </Pressable>
            );
          })}
        </View>
      </Pressable>
    </Modal>
  );
};
